import { Descriptions } from 'antd';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import {
  createGreeting,
  getGreeting,
  updateGreeting,
} from '../../api/homepageManagementOwner.api';
import Button from '../../components/Button';
import ErrorResponse from '../../components/ErrorResponse';
import FileUpload from '../../components/FileUpload';
import SimpleHeader from '../../components/layout/SimpleHeader';
import Loader from '../../components/Loader';
import SelectCampus from '../../components/SelectCampus';
import useAuth from '../../hooks/useAuth';
import useAxios from '../../hooks/useAxios';
import useNotification from '../../hooks/useNotification';
import { IGetGreeting } from '../../models/homepageManagementOwner.model';
import { RootState } from '../../store/rootReducer';

const Block = styled.div`
  .img-preview {
    max-width: 250px;
    display: block;
    margin-bottom: 5px;
  }
`;

const Greeting = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const { currentCampus } = useSelector((state: RootState) => state.auth);
  const { infoNotification, successNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetGreeting>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios<IGetGreeting>();

  const [mode, setMode] = useState('read');
  const [title, setTitle] = useState('');
  const [text, setText] = useState('');
  const [file, setFile] = useState<File>();
  const [fileCode, setFileCode] = useState<number | undefined>();
  const [imgBase64, setImgBase64] = useState('');

  useEffect(() => {
    if (currentCampus) {
      init();
      execute(getGreeting(currentCampus));
    }
  }, [currentCampus]);

  useEffect(() => {
    if (res?.data && !isLoading) {
      setTitle(res.data.title);
      setText(res.data.content);
      setFileCode(res.data.ownerFileCode);
    }
  }, [res, isLoading]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      execute(getGreeting(currentCampus));
      successNotification(t('common.message.save1'));
      setMode('read');
    }
  }, [submitRes, submitIsLoading]);

  const init = () => {
    setMode('read');
    setTitle('');
    setText('');
    setFile(undefined);
  };

  const handleCarrierReturn = (str: string) => {
    return str.replace(/\r\n/gi, '<br />');
  };

  const onSetFile = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      if (base64) setImgBase64(base64.toString());
    };
    reader.readAsDataURL(file);
    setFile(file);
  };

  const onSubmit = () => {
    if (!title) {
      return infoNotification(t('homepage.greeting.message1'));
    }
    if (!text) {
      return infoNotification(t('homepage.greeting.message2'));
    }
    if (mode === 'create' && !file) {
      return infoNotification(t('homepage.greeting.message3'));
    }

    const form = new FormData();
    if (mode === 'create') form.append('campusCode', currentCampus);

    form.append('title', title);
    form.append('content', text);
    if (fileCode) {
      form.append('ownerFileCode', String(fileCode));
    }

    if (file) {
      switch (mode) {
        case 'create':
          form.append('ownerFile', file);
          break;
        case 'update':
          if (fileCode) {
            form.append('ownerFileStatus', 'update');
          } else {
            form.append('ownerFileStatus', 'create');
          }
          form.append('ownerNewFile', file);
          break;
      }
    }

    mode === 'create'
      ? submitExecute(createGreeting(form))
      : submitExecute(updateGreeting(currentCampus, form));
  };

  return (
    <Block className="content scrollbar">
      {isLoading || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={submitError} />
      <SimpleHeader
        iconFile="icon-homepage-big.svg"
        parentMenu={t('common.menu.homepage')}
        currentMenu={t('homepage.greeting.title1')}
      />

      <div className='comm-table__area rowType mt-2'>
        <Descriptions
          bordered
          size="small"
          column={1}
          labelStyle={{ width: '25%' }}
        >
          <Descriptions.Item label={t('common.menu.campus')}>
            <div className="w-150p">
              <SelectCampus />
            </div>
          </Descriptions.Item>
          <Descriptions.Item label={`* ${t('homepage.greeting.column1')}`}>
            {mode === 'read' ? (
              !title && !text ? (
                <div className="text-gray">{`(${t('homepage.greeting.text1')})`}</div>
              ) : (
                <>
                  {title}
                  <hr />
                  <div
                    dangerouslySetInnerHTML={{
                      __html: handleCarrierReturn(text),
                    }}
                  />
                </>
              )
            ) : (
              <>
                <input
                  type="text"
                  className="form-control input-text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
                <div style={{ height: '10px' }} />
                <textarea
                  rows={10}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                />
              </>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={`* ${t('homepage.greeting.column2')}`}>
            {imgBase64 ? (
              <img className="img-preview" src={imgBase64} alt="preview" />
            ) : res?.data?.ownerImg ? (
              <img
                className="img-preview"
                src={res?.data?.ownerImg.fileLocation}
                alt="preview"
              />
            ) : mode === 'read' ? (
                  <div className="text-gray">{`(${t('homepage.greeting.text2')})`}</div>
            ) : null}
            {mode === 'read' ? null : (
              <FileUpload file={file} setFile={onSetFile} />
            )}
            <div className='mt-2'>
              {`(${t('homepage.greeting.text3')})`}
            </div>
          </Descriptions.Item>
        </Descriptions>

        <div className="comm-bx__space">
          <div className="comm-btn__area right">
            {mode === 'read' ? (
              <Button
                className="btn-basic pink-line"
                onClick={() => {
                  if (res?.data) setMode('update');
                  else setMode('create');
                }}
                text={t('common.button.edit')}
                writeAuthYn={writeAuthYn}
              />
            ) : (
              <Button
                className="btn-basic pink"
                onClick={onSubmit}
                text={t('common.button.save')}
              />
            )}
          </div>
        </div>
      </div>
    </Block>
  );
};

export default Greeting;
